import {ReactElement, Suspense, useEffect} from 'react'
import {matchPath, useLocation} from 'react-router-dom'
import {ToastContainer, toast, Bounce} from 'react-toastify'
import classNames from 'classnames'
import {useSelector} from 'react-redux'

import {
  LeftNavigation,
  MainLayout,
  TopNavigation,
  TrialFooter,
  MainContainer,
  RenderIf,
  PageFooter,
} from 'components'
import {
  usePolicy,
  useNativeInfo,
  useActions,
  useCommonFooter,
  useUsersImported,
  useFeatureFlag,
} from 'hooks'
import {AnalyticsRoutesEnum, AuthProviderEnum, OIDCRouteEnum} from 'interfaces'
import {
  createDopamineAssistantStorageScope,
  DopamineAssistantShell,
} from 'features/DopamineAssistant'
import {FeatureHighlightHost} from 'features/FeatureHighlight'
import {selectUser} from 'features/User'
import {SnapshotProvider} from 'context/SnapshotContext'
import Logger from 'services/Logger'
import {GLOBAL_PREFETCH_KEYS} from 'prefetch/prefetch.config'
import {queryClient} from 'services/queryClient'
import {DownloadPollingProvider} from 'features/CASB/context/DownloadPollingContext'
import {AllRoutes} from './routes/Routes'
import {ErrorBoundary} from './ErrorPage'
import {useGlobalPrefetch} from '../prefetch'
import {SuspenseFallback} from './SuspenseFallback'
import {useEntitlementsQuery} from 'features/Entitlements/hooks'

const hideTopNavRoutes = [OIDCRouteEnum.BASE as string]

const Main = () => {
  const {isLeftNavHidden} = useNativeInfo()
  const {isBasePolicyToBeColored} = usePolicy()
  const {isSnackbarActionAvailable, isUserLoggedIn} = useActions()
  const {isDopamineAssistantEnabled} = useFeatureFlag()
  const {emailId, primaryDomain, onboardingCompleted} = useSelector(selectUser)
  // Fetch entitlements of the tenant
  useEntitlementsQuery({
    enabled: isUserLoggedIn,
  })
  // Prime the import status cache on app launch
  useUsersImported()
  const {pathname} = useLocation()
  const isCommonFooterUsed = useCommonFooter()
  const shouldEnableDopamineAssistant =
    isUserLoggedIn && isDopamineAssistantEnabled
  const shouldShowDopamineAssistant =
    shouldEnableDopamineAssistant && pathname.includes('/analytics')
  const isShadowITRoute = Boolean(
    matchPath(pathname, {
      path: AnalyticsRoutesEnum.ANALYTICS_SHADOW_IT,
      exact: true,
      strict: false,
    }),
  )
  const isAIUsageRoute = pathname.startsWith(
    AnalyticsRoutesEnum.ANALYTICS_AI_USAGE,
  )
  const isAnalyticsDetailRoute =
    pathname === AnalyticsRoutesEnum.ANALYTICS_DETAIL
  const dopamineAssistantStorageScope = createDopamineAssistantStorageScope(
    emailId,
    primaryDomain,
  )

  const refetchQueriesOnWindowFocus = (): void => {
    GLOBAL_PREFETCH_KEYS.forEach(key => {
      queryClient.refetchQueries([key])
    })
  }

  // Effect for refetching all global queries on window refocus.
  useEffect(() => {
    window.addEventListener('focus', refetchQueriesOnWindowFocus)
    return () => {
      window.removeEventListener('focus', refetchQueriesOnWindowFocus)
    }
  }, [])

  return (
    <MainLayout
      classes={isBasePolicyToBeColored ? 'bg-dope-teal-100' : 'bg-dope-black'}
    >
      <RenderIf isTrue={isUserLoggedIn && !hideTopNavRoutes.includes(pathname)}>
        <TopNavigation />
      </RenderIf>
      <MainContainer
        hasCommonFooterUsed={isCommonFooterUsed}
        isSnackbarAvailable={isSnackbarActionAvailable}
        className={classNames('flex min-h-0 min-w-0 flex-1', {
          'ai-usage-assistant-layout': isAIUsageRoute,
        })}
      >
        <RenderIf isTrue={shouldEnableDopamineAssistant}>
          <DopamineAssistantShell
            className={classNames({
              'analytics-assistant-constrained:data-[expanded=true]:data-[fullscreen=false]:data-[resized=false]:!w-dopamine-assistant':
                isShadowITRoute || isAnalyticsDetailRoute,
            })}
            isVisible={shouldShowDopamineAssistant}
            key={dopamineAssistantStorageScope ?? 'unscoped'}
            storageScope={dopamineAssistantStorageScope}
          />
        </RenderIf>
        {/* The right panes yield to the Assistant based on the width the
            Analytics content actually has, since the panel can be resized:
            under 1060px the detail panes hide (Shadow IT keeps its pane down
            to 900px) and a restore trigger appears; from those widths on the
            panes come back. */}
        <div
          className={classNames(
            'analytics-app-content-grid grid h-full min-h-0 min-w-0 flex-1 grid-cols-14',
            'peer-data-[fullscreen=true]/dopamine:hidden',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(max-width:1059.98px)]:[&_[data-analytics-layout=policy]]:!grid-cols-[minmax(0,1fr)_0]',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(max-width:1059.98px)]:[&_[data-analytics-layout=productivity]]:!grid-cols-[minmax(0,1fr)_0]',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(max-width:1059.98px)]:[&_[data-analytics-layout=shadow-it]]:!grid-cols-[minmax(0,1fr)_0]',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(max-width:1059.98px)]:[&_[data-analytics-right-panel]]:pointer-events-none',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(max-width:1059.98px)]:[&_[data-analytics-right-panel]]:invisible',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(max-width:1059.98px)]:[&_[data-analytics-right-panel]]:!border-l-0',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(max-width:1059.98px)]:[&_[data-analytics-right-panel]]:opacity-0',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(max-width:1059.98px)]:[&_[data-analytics-right-panel-trigger]:not([data-analytics-right-panel-trigger=detail])]:!inline-flex',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(min-width:900px)]:[&_[data-shadow-it-layout]]:!grid-cols-[minmax(0,1fr)_16rem]',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(min-width:900px)]:[&_[data-shadow-it-layout]_[data-analytics-right-panel]]:!pointer-events-auto',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(min-width:900px)]:[&_[data-shadow-it-layout]_[data-analytics-right-panel]]:!visible',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(min-width:900px)]:[&_[data-shadow-it-layout]_[data-analytics-right-panel]]:!border-l-0.5',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(min-width:900px)]:[&_[data-shadow-it-layout]_[data-analytics-right-panel]]:!opacity-100',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(min-width:900px)]:[&_[data-shadow-it-layout]_[data-analytics-right-panel-trigger]]:!hidden',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(min-width:1060px)]:[&_[data-shadow-it-layout]]:!grid-cols-shadow-it-pane',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(max-width:1059.98px)]:[&_[data-analytics-layout=detail]]:!grid-cols-[minmax(0,1fr)_0]',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(max-width:1059.98px)]:[&_[data-analytics-right-panel=detail]]:pointer-events-none',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(max-width:1059.98px)]:[&_[data-analytics-right-panel=detail]]:invisible',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(max-width:1059.98px)]:[&_[data-analytics-right-panel=detail]]:!border-l-0',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(max-width:1059.98px)]:[&_[data-analytics-right-panel=detail]]:opacity-0',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(max-width:1059.98px)]:[&_[data-analytics-right-panel-trigger=detail]]:!inline-flex',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(min-width:1060px)]:[&_[data-analytics-right-panel=detail]]:!pointer-events-auto',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(min-width:1060px)]:[&_[data-analytics-right-panel=detail]]:!visible',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(min-width:1060px)]:[&_[data-analytics-right-panel=detail]]:!border-l-0.5',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(min-width:1060px)]:[&_[data-analytics-right-panel=detail]]:!opacity-100',
            'peer-data-[expanded=true]/dopamine:[@container_analytics-content_(min-width:1060px)]:[&_[data-analytics-right-panel-trigger=detail]]:!hidden',
          )}
        >
          <div
            className={classNames('col-span-2', {
              hidden: isLeftNavHidden,
            })}
          >
            <LeftNavigation />
          </div>
          <div
            className={classNames('overflow-hidden', {
              'col-span-14': isLeftNavHidden,
              'col-span-12 border-l-0.5 border-gray-400': !isLeftNavHidden,
            })}
          >
            <Suspense fallback={<SuspenseFallback />}>
              <AllRoutes />
            </Suspense>
          </div>
        </div>
      </MainContainer>
      <PageFooter />
      <TrialFooter />
      <RenderIf isTrue={isUserLoggedIn && onboardingCompleted}>
        <FeatureHighlightHost />
      </RenderIf>
    </MainLayout>
  )
}

export default function App(): ReactElement {
  const user = useSelector(selectUser)

  useEffect(() => {
    if (user?.emailId) {
      Logger.SetUserLogScope({
        emailId: user.emailId,
        name: user.name,
        primaryDomain: user.primaryDomain,
      })
    }
  }, [user])

  useGlobalPrefetch({
    shouldRun: Boolean(user?.current?.token),
    authProvider: user?.authProvider as AuthProviderEnum,
  })

  return (
    <ErrorBoundary>
      <ToastContainer
        position={toast.POSITION.BOTTOM_RIGHT}
        hideProgressBar
        transition={Bounce}
      />
      <SnapshotProvider>
        <DownloadPollingProvider>
          <Main />
        </DownloadPollingProvider>
      </SnapshotProvider>
    </ErrorBoundary>
  )
}
